/**
 * FigSpecForm — 그림 명세(숫자·식)를 고치는 칸.
 *
 * 계약 §2 의 문법과 **§9-1 문법 v2**(2026-08-30)를 그대로 다룬다. 종류가 10종이라 폼을 손으로
 * 열 벌 쓰지 않고 figSpecSchema.js 의 표 하나로 그린다 — 백엔드가 문법을 늘리면 그 표만 늘린다.
 *
 * 값이 바뀌면 부모가 즉시 서버에 그려 달라고 보낸다(useSpecRender). 여기서는 **검사하지 않는다** —
 * 무엇이 왜 틀렸는지는 서버의 issues 가 훨씬 정확하고, 여기서 미리 막으면 멀쩡한 값까지 막는다.
 * 다만 **빈 칸은 보내지 않는다**(pruneSpec) — 방금 지운 칸 때문에 기각당하면 사용자는 이유를 모른다.
 *
 * ── 줄이 둘이다(2026-08-30) ──
 * 윗줄 = 형태를 정하는 값(좌표·식), 아랫줄 = 꾸미기(점선·굵기·화살표·같은 길이·채우기·빗금).
 * 아랫줄은 **값이 하나라도 있으면 저절로 펴진다.** §9-3 이후 AI 가 직각·점선·빗금을 넣어 보내는데,
 * 늘 접어 두면 그것이 접힌 채라 사람이 있는 줄도 모른다. 반대로 늘 펴면 평범한 선분 하나가 11칸이 된다.
 *
 * ── 자리가 좁다(2026-09-06 재배치) ──
 * 이 폼이 사는 칸은 **폭 500px 안팎 · 높이 320px** 다(FigureModal 오른쪽 Pane 의 `h-80`). 그 안에
 * 원소 열 종류와 꾸미기 칸이 전부 들어간다. 오너 지시(「시인성·가독성 좋게 재정렬 · 필수가 아닌
 * 안내 텍스트는 모두 제거」)에 따라 넷을 지킨다.
 *
 * ① **설명 문장을 화면에 두지 않는다.** 아래 있던 식 문법 문단(9줄)·원소마다의 힌트·필수 칸
 *    부연을 걷었다. 틀린 값의 사유는 서버 issues 가 훨씬 정확히 말하고, 이 칸의 세로는 값을 넣는
 *    데 쓴다. **잃으면 곤란한 것**(점의 dx·dy 가 무슨 거리인지, 호의 각도가 반시계인지)은 지우지
 *    않고 스키마의 `title` 로 옮겼다 — 마우스를 올려야 뜨므로 화면 글자를 늘리지 않는다.
 *    원소 힌트도 같은 식으로 살아 있다(아래 [추가] 버튼의 title).
 * ② **줄 오른쪽 버튼을 고정한다.** 종전에는 `ml-auto` 를 낀 빈 span 하나로 밀어 두어, 칸이
 *    감길 때마다 [꾸미기]·[지우기]가 어느 줄 끝에 붙을지 달라졌다. 값 칸 묶음과 버튼 묶음을
 *    형제로 갈라 늘 오른쪽 위에 선다.
 * ③ **줄 왼쪽에 순번을 적는다.** 계약이 원소를 순서로 지정하는데(고유 id 가 없다) 화면에만 그
 *    숫자가 없었다 — aria-label 은 이미 「n번째」라 부르고 있었다. 왼쪽에 서면 값 칸의 정렬
 *    기준선도 함께 생긴다.
 * ④ **가로·세로를 두 줄로 세운다.** 범위 넷·눈금 둘·체크 둘·글자 크기가 한 줄에 뒤섞여 있던
 *    것을 축마다 한 줄로 갈랐다. 그러면 눈금 칸의 이름이 「가로 눈금」일 필요가 없다 — 줄이
 *    이미 어느 축인지 말한다.
 *
 * ── 소수점이 안 들어갔다(2026-09-09) ──
 * 값 칸은 이제 **자기가 친 글자를 들고 있는다**(useDraftText). 부모의 명세 값만 보고 그리면
 * 「16.」 이 그 자리에서 16 으로 되씹혀 점이 지워졌다 — 소수를 칠 방법이 아예 없었다.
 * 종전에는 목록 칸(꼭짓점·눈금)만 이 처리를 가지고 있었다. 자세한 것은 그 훅의 주석.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Trash2, Code2, Palette } from 'lucide-react';
import { Button } from '../common';
import {
  FIG_ELEMENTS, FIG_SHADE_FIELDS, FIG_SHADE_OPTS, FIG_AXIS_FIELDS, FIG_FONT_FIELDS,
  ptsToText, textToPts, pairToText, textToPair, numsToText, textToNums,
} from './figSpecSchema';

const W = { sm: 'w-16', md: 'w-24', lg: 'min-w-32 flex-1' };
/** 입력·라벨의 생김새를 한 곳에 둔다 — 열 군데에 손으로 적으면 한 벌만 고쳐져 칸마다 높이가 갈린다. */
const INPUT = 'rounded border border-gray-200 px-1.5 py-1 text-xs text-gray-800 focus:border-primary focus:outline-none disabled:bg-gray-50';
const LABEL = 'flex items-center gap-1 text-[11px] text-gray-500';
/** 줄 안의 값 칸 묶음 — 세로로 감길 때 줄 간격을 벌려 둔다(gap-y). 붙어 감기면 두 줄이 한 줄로 읽힌다. */
const ROW_FIELDS = 'flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1';
const ICON_BTN = 'rounded p-1 text-gray-400 hover:bg-white disabled:opacity-40';

/**
 * 축 두 줄. 눈금 칸의 이름을 여기서 「눈금」으로 갈아 쓴다 — 스키마(FIG_AXIS_FIELDS)가 정본이고
 * 「가로 눈금 / 세로 눈금」이라는 이름은 **한 줄에 넷이 서던 옛 배치의 사정**이었다. 지금은 줄이
 * 축을 말하므로 그 앞머리가 중복이다. 키·형식(nums)은 스키마 것을 그대로 쓴다.
 */
const AXIS_ROWS = [['x', '가로', 'xticks'], ['y', '세로', 'yticks']].map(([k, name, tk]) => {
  const f = FIG_AXIS_FIELDS.find((x) => x.k === tk);
  return { k, name, tick: f ? { ...f, label: '눈금' } : null };
});

/**
 * 축 범위 두 칸(`x`·`y` 배열의 0번·1번)은 스키마에 항목이 없다 — 배열의 한 자리라 이름을 걸 데가
 * 없어서다. 숫자 칸의 규격만 빌려 쓰고, 이름은 aria-label 로 준다.
 */
const RANGE_F = { num: true };

/**
 * 값 칸이 **자기가 친 글자를 들고 있는다** — 치는 동안에는 화면 문자열이 정본이다.
 *
 * 왜 필요한가: 칸이 바뀔 때마다 값은 `문자열 → 명세 값 → 문자열` 로 되씹히는데, 그 왕복에서
 * **아직 숫자가 되지 못한 중간 글자가 지워진다.** 「16.」 은 `Number` 를 지나면 16 이고,
 * 되돌아온 「16」 에는 점이 없다 — 그래서 **소수점을 칠 수가 없었다**(2026-09-09 오너 보고).
 * 같은 자리에서 끝자리 0(「0.50」)도, 목록 칸의 끝 공백(「1 」의 둘째 숫자)도, 좌표 한 쌍의
 * 뒤쪽 소수점(「16,0.」)도 사라진다. 종전에는 목록 칸(pts·nums)만 이 처리를 가지고 있었다.
 *
 * 밖에서 값이 바뀔 때(이력 불러오기·손잡이로 끌기)는 덮어써야 한다. 그래서 **내가 방금 낸 값이
 * 그대로 돌아온 것**만 무시한다 — 그 판정에 쓰는 것이 lastRef 다.
 *
 * ⚠ 이 칸은 **명세 값을 받아 명세 값을 낸다**(문자열이 아니다). 변환을 부르는 자리가 여기 하나여야
 * lastRef 의 예측(「부모는 내가 낸 값을 그대로 담는다」)이 성립한다 — 부르는 쪽에서 한 번 더
 * 변환하면 그 예측이 어긋나고, 꼭짓점 칸은 두 번 변환하면 값이 통째로 빈다(ptsToText).
 */
function useDraftText(f, value, onChange) {
  const [text, setText] = useState(() => asText(f, value));
  const lastRef = useRef(asText(f, value));
  useEffect(() => {
    const incoming = asText(f, value);
    if (incoming !== lastRef.current) {
      lastRef.current = incoming;
      setText(incoming);
    }
  }, [f, value]);
  const emit = (t) => {
    setText(t);
    const parsed = toValue(f, t);
    lastRef.current = asText(f, parsed); // 부모가 되돌려줄 문자열을 미리 적어 둔다
    onChange(parsed);
  };
  return [text, emit];
}

/** 글자를 치는 칸 하나 — 라벨 없이 input 만. 축 범위처럼 스키마 밖의 자리도 이것을 쓴다. */
function DraftInput({ f, value, onChange, disabled, className, ariaLabel }) {
  const [text, emit] = useDraftText(f, value, onChange);
  return (
    <input
      type="text"
      inputMode={f.num ? 'decimal' : 'text'}
      aria-label={ariaLabel}
      value={text}
      placeholder={f.ph || ''}
      onChange={(e) => emit(e.target.value)}
      disabled={disabled}
      className={className}
    />
  );
}

/**
 * 칸 하나. 종류는 스키마가 정한다(num·bool·sel·pts·pair·nums).
 *
 * `f.title` 은 **화면에 안 뜨는 설명**이다 — 문장을 걷어내면서, 라벨만으로는 뜻이 안 서는 칸
 * (점의 dx·dy, 호의 시작°·끝°)의 설명이 갈 곳으로 삼았다.
 */
function Field({ f, value, onChange, disabled }) {
  if (f.bool) {
    return (
      <label className="flex items-center gap-1 text-[11px] text-gray-600" title={f.title}>
        <input
          type="checkbox"
          /* 새 꾸미기 칸은 **기본이 꺼짐**이다. 종전부터 있던 `close` 만 켜짐이라(계약 §2 의 예시)
             그것에만 defTrue 를 단다 — 하나로 뭉치면 체크 안 한 그림에 점선이 그어진다. */
          checked={f.defTrue ? value !== false : value === true}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
          className="size-3.5 accent-primary"
        />
        {f.label}
      </label>
    );
  }
  if (f.sel) {
    // 첫 항목이 빈 값(「없음」)이다 — 고르면 pruneSpec 이 키를 통째로 뺀다(빈 문자열 규칙).
    return (
      <label className={LABEL} title={f.title}>
        <span className="shrink-0">{f.label}</span>
        <select
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="rounded border border-gray-200 py-1 pl-1.5 pr-5 text-xs text-gray-800 focus:border-primary focus:outline-none disabled:bg-gray-50"
        >
          {f.sel.map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)}
        </select>
      </label>
    );
  }
  return (
    <label className={`${LABEL} ${f.w === 'lg' ? 'min-w-32 flex-1' : ''}`} title={f.title}>
      <span className="shrink-0">{f.label}</span>
      <DraftInput f={f} value={value} onChange={onChange} disabled={disabled}
        className={`${W[f.w] || W.md} ${INPUT}`} />
    </label>
  );
}

/** 화면의 문자열을 명세 값으로. 숫자 칸은 숫자로, 못 읽으면 원문을 남겨 서버가 말하게 한다. */
function toValue(f, raw) {
  if (f.bool || f.sel) return raw;
  if (f.pts) return textToPts(raw);
  if (f.pair) return textToPair(raw);
  if (f.nums) return textToNums(raw);
  if (f.num) {
    const s = String(raw).trim();
    if (s === '') return undefined;
    const n = Number(s);
    return Number.isFinite(n) ? n : raw;
  }
  return raw;
}

function fromValue(f, v) {
  if (f.pts) return ptsToText(v);
  if (f.pair) return pairToText(v);
  if (f.nums) return numsToText(v);
  return v;
}

/** 명세 값 → 화면 글자. 목록·좌표쌍은 fromValue 가 잇고, 숫자와 빈 값은 여기서 글자가 된다. */
function asText(f, v) {
  const t = fromValue(f, v);
  return t == null ? '' : String(t);
}

/** 이 줄의 꾸미기 칸에 값이 하나라도 들었나 — 들었으면 접지 않는다(AI 가 넣은 것이 숨지 않게). */
function hasOptValue(row, opts) {
  if (!Array.isArray(opts) || row == null) return false;
  return opts.some((f) => {
    const v = row[f.k];
    if (v == null) return false;
    if (typeof v === 'string') return v.trim() !== '';
    if (typeof v === 'boolean') return v === true;
    return true;
  });
}

/** 원소 상자의 머리줄 — 이름은 굵게, 오른쪽에 그 상자의 동작 하나. 열 상자가 같은 규격을 쓴다. */
function SectionHead({ name, children }) {
  return (
    <div className="flex items-center gap-2 px-2.5 py-1.5">
      <span className="text-xs font-bold text-gray-700">{name}</span>
      <span className="ml-auto flex items-center">{children}</span>
    </div>
  );
}

export default function FigSpecForm({ spec, onChange, disabled = false }) {
  const [rawOpen, setRawOpen] = useState(false);
  const [rawText, setRawText] = useState('');
  const [rawError, setRawError] = useState(null);
  /**
   * 손으로 편 꾸미기 줄들 — `"segments.0"` 꼴. **값이 든 줄은 여기 없어도 펴진다.**
   * 줄을 지우면 뒤 순번이 당겨져 이 표시가 옆 줄로 옮겨 갈 수 있는데, 그때 보이는 것은
   * 빈 꾸미기 줄 하나라 손해가 없다. 원소에 고유 id 가 없어(계약이 순서로 지정한다) 피할 길도 없다.
   */
  const [optOpen, setOptOpen] = useState(() => new Set());
  const toggleOpt = (id) => setOptOpen((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // spec 이 null 일 때도 같은 참조를 유지한다 — 매 렌더 새 객체를 만들면 아래 useMemo 가
  // 항상 다시 돌고, 훅 규칙 검사에도 걸린다.
  const s = useMemo(() => spec || {}, [spec]);
  const set = (patch) => onChange({ ...s, ...patch });

  const setElem = (key, i, fieldKey, v) => {
    const arr = Array.isArray(s[key]) ? [...s[key]] : [];
    arr[i] = { ...(arr[i] || {}), [fieldKey]: v };
    set({ [key]: arr });
  };
  const addElem = (key) => {
    const arr = Array.isArray(s[key]) ? [...s[key]] : [];
    arr.push({});
    set({ [key]: arr });
  };
  const removeElem = (key, i) => {
    const arr = (Array.isArray(s[key]) ? s[key] : []).filter((_, k) => k !== i);
    set({ [key]: arr });
  };

  const rangeVal = (k, i) => (Array.isArray(s[k]) ? s[k][i] : undefined);
  /** 글자를 숫자로 옮기는 일은 칸이 이미 했다(DraftInput) — 여기서는 배열의 그 자리에 넣기만 한다. */
  const setRange = (k, i, v) => {
    const cur = Array.isArray(s[k]) ? [...s[k]] : [];
    while (cur.length < 2) cur.push(undefined);
    cur[i] = v;
    set({ [k]: cur });
  };

  const usedKeys = useMemo(
    () => FIG_ELEMENTS.filter((e) => Array.isArray(s[e.key]) && s[e.key].length > 0).map((e) => e.key),
    [s],
  );

  return (
    <div className="space-y-2">
      {/**
        * 그림 설명(alt) — **읽는 글자다. 고치는 칸이 아니다**(오너 지시 2026-09-06).
        *
        * 나머지 칸은 전부 그림을 그리는 값인데 이것 하나만 그림을 **말로 옮긴 문장**이고,
        * 그 문장은 AI 가 그림을 만들 때 함께 만들어 보낸다. 사람이 여기서 손볼 값이 아니어서
        * 입력칸으로 두면 고칠 것이 하나 더 있는 것처럼 읽힌다. 그래서 회색 카드에 글자로 얹는다.
        *
        * **맨 위에 선다**(오너 지시 2026-09-06). 이 칸을 여는 사람이 가장 먼저 알아야 하는 것은
        * 「지금 보고 있는 그림이 무엇인가」다 — 좌표 범위나 눈금보다 앞이다.
        *
        * 고쳐야 할 때는 아래 **[원문으로 고치기 (JSON)]** 에서 한다 — 그쪽이 명세를 통째로 보낸다.
        * ⚠ 이 값이 비면 저장이 잠긴다(FigureModal 의 `altOk`, canSave 의 곱 조건 — 서버가 alt
        * 없는 명세를 기각한다 · 계약 §2). 그때 화면이 처방하는 곳도 그 JSON 이다.
        */}
      <div className="flex items-start gap-1.5 rounded-lg bg-gray-100 p-2">
        <span className="shrink-0 text-[11px] text-gray-500">그림 설명</span>
        <p className="min-w-0 flex-1 text-[11px] leading-relaxed text-gray-700">
          {s.alt?.trim() ? s.alt : <span className="text-gray-400">—</span>}
        </p>
      </div>
      {/* 그림 판 — 축마다 한 줄(범위 둘 + 눈금), 그 아래 그림 전체에 걸리는 값(축·격자·글자 크기).
          계약 §2 에서 x·y 가 필수다. */}
      <div className="space-y-1.5 rounded-xl border border-gray-200 p-2.5">
        {AXIS_ROWS.map(({ k, name, tick }) => (
          <div key={k} className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="w-6 shrink-0 text-[11px] text-gray-500">{name}</span>
            <DraftInput f={RANGE_F} ariaLabel={`${name} 범위 시작`} disabled={disabled}
              value={rangeVal(k, 0)} onChange={(v) => setRange(k, 0, v)}
              className={`w-16 ${INPUT}`} />
            <span className="text-[11px] text-gray-400">~</span>
            <DraftInput f={RANGE_F} ariaLabel={`${name} 범위 끝`} disabled={disabled}
              value={rangeVal(k, 1)} onChange={(v) => setRange(k, 1, v)}
              className={`w-16 ${INPUT}`} />
            {tick && (
              <Field f={tick} disabled={disabled}
                value={s[tick.k]}
                onChange={(v) => set({ [tick.k]: v })} />
            )}
          </div>
        ))}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-gray-100 pt-1.5">
          <label className="flex items-center gap-1 text-[11px] text-gray-600">
            <input type="checkbox" checked={s.axes !== false} onChange={(e) => set({ axes: e.target.checked })} disabled={disabled}
              className="size-3.5 accent-primary" />
            좌표축
          </label>
          <label className="flex items-center gap-1 text-[11px] text-gray-600">
            <input type="checkbox" checked={s.grid === true} onChange={(e) => set({ grid: e.target.checked })} disabled={disabled}
              className="size-3.5 accent-primary" />
            격자
          </label>
          {/* 그림 전체 글자 크기(계약 §11). 축·격자와 함께 「그림 전체에 걸리는 값」이라 같은 줄이고,
              비우면 키가 빠져 서버 기본 13px 이다. */}
          {FIG_FONT_FIELDS.map((f) => (
            <Field key={f.k} f={f} disabled={disabled}
              value={s[f.k]}
              onChange={(v) => set({ [f.k]: v })} />
          ))}
        </div>
      </div>


      {/* 원소들 — 쓰고 있는 종류만 펼쳐 보이고, 나머지는 아래 [추가]로 연다 */}
      {FIG_ELEMENTS.filter((e) => usedKeys.includes(e.key)).map((el) => (
        <div key={el.key} className="rounded-xl border border-gray-200">
          <SectionHead name={el.name}>
            {/* 글자 없이 [+] 하나다 — 상자마다 [＋ 추가] 알약이 서면 그것이 상자 이름보다 무거워
                보인다. 무엇을 더하는지는 상자 이름이 말하고, 종류 설명은 title 로 남는다. */}
            <button type="button" onClick={() => addElem(el.key)} disabled={disabled}
              aria-label={`${el.name} 하나 더`} title={el.hint}
              className={`${ICON_BTN} hover:text-gray-700`}>
              <Plus className="size-3.5" />
            </button>
          </SectionHead>
          <div className="space-y-1 border-t border-gray-100 p-1.5">
            {(s[el.key] || []).map((row, i) => {
              const rowId = `${el.key}.${i}`;
              const optsOn = hasOptValue(row, el.opts) || optOpen.has(rowId);
              return (
                // eslint-disable-next-line react/no-array-index-key -- 원소에 고유 id 가 없다. 순서가 곧 자리다.
                <div key={i} className="rounded-lg bg-gray-50 p-1.5">
                  <div className="flex items-start gap-1.5">
                    {/* 순번 — 계약이 원소를 순서로 지정한다. 왼쪽에 서면 값 칸의 기준선도 된다. */}
                    <span className="w-3 shrink-0 pt-2 text-[10px] leading-none text-gray-400">{i + 1}</span>
                    <div className={ROW_FIELDS}>
                      {el.fields.map((f) => (
                        <Field key={f.k} f={f} disabled={disabled}
                          value={row?.[f.k]}
                          onChange={(v) => setElem(el.key, i, f.k, v)} />
                      ))}
                    </div>
                    {/* 값 칸과 **형제**로 둔다 — 값이 몇 줄로 감기든 버튼은 오른쪽 위에 그대로 선다. */}
                    <div className="flex shrink-0 items-center gap-0.5">
                      {el.opts?.length > 0 && (
                        <button type="button" onClick={() => toggleOpt(rowId)} disabled={disabled}
                          aria-pressed={optsOn}
                          aria-label={`${el.name} ${i + 1}번째 꾸미기`}
                          title={`${el.name} 꾸미기`}
                          className={`${ICON_BTN} ${optsOn ? 'text-primary' : ''}`}>
                          <Palette className="size-3.5" />
                        </button>
                      )}
                      <button type="button" onClick={() => removeElem(el.key, i)} disabled={disabled}
                        aria-label={`${el.name} ${i + 1}번째 지우기`}
                        title={`${el.name} ${i + 1}번째 지우기`}
                        className={`${ICON_BTN} hover:text-danger`}>
                        <Trash2 className="size-3.5" />
                      </button>
                    </div>
                  </div>
                  {/* 꾸미기는 **흰 상자**로 한 단 안쪽에 넣는다 — 줄(회색) 안의 부속임이 색으로 보인다.
                      선 하나로만 갈라 두면 칸이 아홉인 선분에서 어디까지가 그 줄인지 흐려진다. */}
                  {optsOn && (
                    <div className="ml-4 mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-gray-200 bg-white px-1.5 py-1">
                      {el.opts.map((f) => (
                        <Field key={f.k} f={f} disabled={disabled}
                          value={row?.[f.k]}
                          onChange={(v) => setElem(el.key, i, f.k, v)} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* 칠하기 — 배열이 아니라 객체 하나다(계약 §2). 채우기·빗금은 §9-1 로 붙었다.
          줄이 하나뿐이라 순번을 적지 않는다. 나머지 규격은 원소 상자와 같다. */}
      {s.shade != null && (
        <div className="rounded-xl border border-gray-200">
          <SectionHead name="칠하기">
            <button type="button" onClick={() => set({ shade: null })} disabled={disabled}
              aria-label="칠하기 지우기" title="칠하기 지우기"
              className={`${ICON_BTN} hover:text-danger`}>
              <Trash2 className="size-3.5" />
            </button>
          </SectionHead>
          <div className="border-t border-gray-100 p-1.5">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg bg-gray-50 p-1.5">
              {[...FIG_SHADE_FIELDS, ...FIG_SHADE_OPTS].map((f) => (
                <Field key={f.k} f={f} disabled={disabled}
                  value={s.shade?.[f.k]}
                  onChange={(v) => set({ shade: { ...(s.shade || {}), [f.k]: v } })} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 없는 종류 열기 — 종류 설명(hint)은 여기 버튼의 title 에만 남는다. */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[11px] text-gray-400">추가</span>
        {FIG_ELEMENTS.filter((e) => !usedKeys.includes(e.key)).map((el) => (
          <Button key={el.key} size="xs" variant="secondary" title={el.hint}
            onClick={() => addElem(el.key)} disabled={disabled}>
            {el.name}
          </Button>
        ))}
        {s.shade == null && (
          <Button size="xs" variant="secondary" title="두 곡선 사이를 칠한다"
            onClick={() => set({ shade: {} })} disabled={disabled}>칠하기</Button>
        )}
      </div>

      {/* 통째로 고치기 — 여러 원소를 한꺼번에 붙여 넣거나, 폼에 없는 키를 다룰 때 */}
      <div className="rounded-xl border border-gray-100">
        <button type="button" onClick={() => { setRawOpen((v) => { const n = !v; if (n) { setRawText(JSON.stringify(spec ?? {}, null, 2)); setRawError(null); } return n; }); }}
          className="flex w-full items-center gap-1.5 px-2.5 py-1.5 text-[11px] text-gray-500 hover:bg-gray-50">
          <Code2 className="size-3.5" />
          {rawOpen ? '원문 접기' : '원문으로 고치기 (JSON)'}
        </button>
        {rawOpen && (
          <div className="space-y-1.5 border-t border-gray-100 p-2.5">
            <textarea value={rawText} onChange={(e) => setRawText(e.target.value)} disabled={disabled} rows={10} spellCheck={false}
              className="w-full rounded border border-gray-200 p-2 font-mono text-[11px] leading-relaxed focus:border-primary focus:outline-none disabled:bg-gray-50" />
            {rawError && <p role="alert" className="text-[11px] text-danger">{rawError}</p>}
            <Button size="xs" variant="secondary" disabled={disabled} onClick={() => {
              try {
                const parsed = JSON.parse(rawText);
                if (parsed == null || typeof parsed !== 'object' || Array.isArray(parsed)) {
                  setRawError('명세는 객체 하나여야 합니다 — { } 로 감싸 주세요.');
                  return;
                }
                setRawError(null);
                onChange(parsed);
              } catch (err) {
                setRawError(`JSON 형식이 아닙니다 — ${err.message}`);
              }
            }}>
              원문 적용
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
